import { stopPostHogSessionRecording } from '@client/utils/feature-flags/posthogClient';
import * as Sentry from '@sentry/react';
import type { AnyRouter } from '@tanstack/react-router';
import { preventRecordingForLocation } from './recording-privacy';

/** Stop as navigation begins without adding a history blocker. The resolved listener covers
 * history traversal and redirects. The Replay transport independently drops
 * secret-bearing recordings, including the traversal-to-listener window.
 */
export function trackSecretLocationRecordingGate(router: AnyRouter): () => void {
    const stopForLocation = (pathname: string, search: string) => {
        if (preventRecordingForLocation(pathname, search)) {
            // Recorder failures must never abort navigation or prevent the other
            // recorder from stopping. The transport guard also fails closed.
            try {
                void Sentry.getReplay()
                    ?.stop({ flush: false })
                    .catch(() => {});
            } catch {
                // The SDK may throw before returning a promise.
            }
            try {
                stopPostHogSessionRecording();
            } catch {
                // Telemetry must not interfere with routing.
            }
        }
    };
    const unsubscribeBefore = router.subscribe('onBeforeNavigate', ({ toLocation }) => {
        stopForLocation(toLocation.pathname, toLocation.searchStr);
    });
    const unsubscribe = router.subscribe('onResolved', ({ toLocation }) => {
        stopForLocation(toLocation.pathname, toLocation.searchStr);
    });
    return () => {
        unsubscribeBefore();
        unsubscribe();
    };
}
